/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Plus,
  Search,
  Loader2,
  ArrowUpDown,
  X,
  Check,
  Edit2,
  Trash2,
  DollarSign,
  AlertCircle,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  fetchVendorCharges,
  createVendorCharge,
  updateVendorCharge,
  deleteVendorCharge,
  validateCharge,
} from './api/ChargeManagementApi';
import { useUser } from '@/contexts/UserContext';
import BottomNav from '../../components/layouts/Footer/BottomNav';

const CHARGE_NAME_OPTIONS = [
  'CONVENIENCE_FEE',
  'HANDLING_FEE',
  'PACKAGING_FEE',
  'PLATFORM_FEE',
  'SERVICE_FEE',
  'TAX',
];

const defaultFormData = {
  name: 'PLATFORM_FEE',
  chargeType: 'FIXED',
  value: '',
  actualValue: '',
  vendorBears: '',
  chargeableToUser: true,
  active: true,
  displayOrder: 0,
};

export default function ChargeManagement() {
  const navigate = useNavigate();
  const { user, isInitialized } = useUser();

  const [charges, setCharges] = useState([]);
  const [filteredCharges, setFilteredCharges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [editingCharge, setEditingCharge] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [formData, setFormData] = useState(defaultFormData);

  const vendorId = user?.id;

  // Fetch charges
  const fetchCharges = async (id) => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await fetchVendorCharges(id);
      setCharges(Array.isArray(data) ? data : []);
      setFilteredCharges(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to fetch charges');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Load charges on mount
  useEffect(() => {
    if (isInitialized && vendorId) {
      fetchCharges(vendorId);
    }
  }, [vendorId, isInitialized]);

  // Handle form input
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Create charge
  const handleCreateCharge = async () => {
    const validation = validateCharge(formData);
    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }

    if (!vendorId) {
      toast.error('Vendor ID not found');
      return;
    }

    try {
      const payload = {
        ...formData,
        value: parseFloat(formData.value),
        actualValue: parseFloat(formData.actualValue),
        vendorBears: parseFloat(formData.vendorBears),
        displayOrder: parseInt(formData.displayOrder),
      };

      await createVendorCharge(vendorId, payload);
      toast.success('Charge created successfully');
      setIsAddDialogOpen(false);
      setFormData(defaultFormData);
      fetchCharges(vendorId);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to create charge');
    }
  };

  // Update charge
  const handleUpdateCharge = async () => {
    const validation = validateCharge(formData);
    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }

    if (!editingCharge || !vendorId) {
      toast.error('Charge or vendor ID not found');
      return;
    }

    try {
      const payload = {
        ...formData,
        value: parseFloat(formData.value),
        actualValue: parseFloat(formData.actualValue),
        vendorBears: parseFloat(formData.vendorBears),
        displayOrder: parseInt(formData.displayOrder),
      };

      await updateVendorCharge(vendorId, editingCharge.id, payload);
      toast.success('Charge updated successfully');
      setIsEditDialogOpen(false);
      setFormData(defaultFormData);
      setEditingCharge(null);
      fetchCharges(vendorId);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update charge');
    }
  };

  // Delete charge
  const handleDeleteCharge = async (chargeId) => {
    if (!vendorId) {
      toast.error('Vendor ID not found');
      return;
    }

    try {
      await deleteVendorCharge(vendorId, chargeId);
      toast.success('Charge deleted successfully');
      setIsDeleteDialogOpen(false);
      setDeleteConfirmId(null);
      fetchCharges(vendorId);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete charge');
    }
  };

  // Open edit dialog
  const openEditDialog = (charge) => {
    setEditingCharge(charge);
    setFormData({
      name: charge.name,
      chargeType: charge.chargeType,
      value: charge.value.toString(),
      actualValue: charge.actualValue.toString(),
      vendorBears: charge.vendorBears.toString(),
      chargeableToUser: charge.chargeableToUser,
      active: charge.active,
      displayOrder: charge.displayOrder,
    });
    setIsEditDialogOpen(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData(defaultFormData);
    setEditingCharge(null);
  };

  // Filter charges
  useEffect(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      setFilteredCharges([...charges]);
    } else {
      const filtered = charges.filter(
        (charge) =>
          charge.name.toLowerCase().includes(query) ||
          charge.chargeType.toLowerCase().includes(query)
      );
      setFilteredCharges(filtered);
    }
  }, [searchQuery, charges]);

  // Handle sort
  const handleSort = () => {
    const sorted = [...filteredCharges].sort((a, b) => {
      const comparison = a.name.localeCompare(b.name);
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    setFilteredCharges(sorted);
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  // Prepare chart data
  const chartData = useMemo(() => {
    return filteredCharges.map((charge) => ({
      name: charge.name.replace(/_/g, ' '),
      'Charge Value': charge.value,
      'Actual Value': charge.actualValue,
      'Vendor Bears': charge.vendorBears,
    }));
  }, [filteredCharges]);

  const getChargeSymbol = (type) => (type === 'PERCENTAGE' ? '%' : '₹');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg">
                  <DollarSign className="w-6 h-6 text-white" />
                </div>
                Charge Management
              </h1>
              <p className="text-sm text-gray-500 mt-2">
                Manage vendor charges (fees, taxes, and additional costs)
              </p>
            </div>
            {vendorId && (
              <Button
                onClick={() => setIsAddDialogOpen(true)}
                className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white shadow-md hover:shadow-lg transition-all duration-200"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Charge
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8 pb-20 md:pb-8">
        {/* Search and Sort */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
            <Input
              placeholder="Search charges by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 border-slate-300 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>
          <Button
            onClick={handleSort}
            variant="outline"
            className="border-slate-300 text-gray-700 hover:bg-slate-50"
          >
            <ArrowUpDown className="h-4 w-4 mr-2" />
            Sort ({sortOrder === 'asc' ? 'A-Z' : 'Z-A'})
          </Button>
        </div>

        {/* Two Column Layout: Table + Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Charges Table */}
          <div className="lg:col-span-2">
            <Card className="shadow-lg border-slate-200">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex justify-center items-center py-16">
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 text-purple-600 animate-spin mx-auto mb-3" />
                      <p className="text-gray-500 font-medium">Loading charges...</p>
                    </div>
                  </div>
                ) : filteredCharges.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            Charge Name
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            Type
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            Value
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            Status
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {filteredCharges.map((charge) => (
                          <tr
                            key={charge.id}
                            className="hover:bg-slate-50 transition-colors duration-150"
                          >
                            <td className="px-6 py-4">
                              <span className="font-semibold text-gray-900">
                                {charge.name.replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100 font-semibold">
                                {charge.chargeType}
                              </Badge>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm text-gray-700">
                                {charge.value}
                                {getChargeSymbol(charge.chargeType)}
                              </p>
                            </td>
                            <td className="px-6 py-4">
                              <Badge
                                className={`font-semibold ${
                                  charge.active
                                    ? 'bg-green-100 text-green-800 hover:bg-green-100'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                {charge.active ? '✓ Active' : '✗ Inactive'}
                              </Badge>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => openEditDialog(charge)}
                                  className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all duration-150"
                                  title="Edit charge"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setDeleteConfirmId(charge.id);
                                    setIsDeleteDialogOpen(true);
                                  }}
                                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-150"
                                  title="Delete charge"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="py-16 text-center">
                    <div className="mb-4 flex justify-center">
                      <div className="p-3 bg-purple-50 rounded-full">
                        <DollarSign className="w-8 h-8 text-purple-300" />
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No charges found</h3>
                    <p className="text-gray-500 mb-6">
                      {searchQuery
                        ? 'Try adjusting your search terms'
                        : 'Create your first charge to get started'}
                    </p>
                    {!searchQuery && vendorId && (
                      <Button
                        onClick={() => setIsAddDialogOpen(true)}
                        className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create First Charge
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right: Bar Chart */}
          {!loading && filteredCharges.length > 0 && (
            <div className="lg:col-span-1">
              <Card className="shadow-lg border-slate-200 sticky top-24">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-purple-50 border-b border-slate-200 py-3">
                  <CardTitle className="text-sm font-semibold text-gray-900">
                    Charges Comparison
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11 }}
                        stroke="#6b7280"
                        interval={0}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis tick={{ fontSize: 12 }} stroke="#6b7280" />
                      <Tooltip
                        formatter={(value) => `${value.toFixed(2)}`}
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.5rem',
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                      <Bar dataKey="Charge Value" fill="#a855f7" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Actual Value" fill="#d8b4fe" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Vendor Bears" fill="#6b21a8" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>

                  <div className="mt-4 pt-4 border-t border-gray-200 space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Charges</span>
                      <span className="font-semibold text-gray-900">{filteredCharges.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Active</span>
                      <span className="font-semibold text-gray-900">
                        {filteredCharges.filter((c) => c.active).length}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Avg Charge</span>
                      <span className="font-semibold text-gray-900">
                        ₹
                        {(
                          filteredCharges.reduce((sum, c) => sum + c.value, 0) /
                          filteredCharges.length
                        ).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Add Charge Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-4 border-b border-slate-200">
            <DialogTitle className="text-xl font-bold text-gray-900">Add New Charge</DialogTitle>
            <DialogDescription className="mt-1">
              Create a new vendor charge for fees and costs. All values marked with * are required.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {/* Basic Information Section */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <div className="h-1 w-1 rounded-full bg-purple-600" />
                Basic Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-sm font-semibold text-gray-700 mb-2 block">
                    Charge Name *
                  </Label>
                  <select
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm font-medium focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  >
                    {CHARGE_NAME_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Select the type of charge</p>
                </div>

                <div>
                  <Label
                    htmlFor="chargeType"
                    className="text-sm font-semibold text-gray-700 mb-2 block"
                  >
                    Charge Type *
                  </Label>
                  <select
                    id="chargeType"
                    name="chargeType"
                    value={formData.chargeType}
                    onChange={handleInputChange}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm font-medium focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  >
                    <option value="FIXED">Fixed Amount (₹)</option>
                    <option value="PERCENTAGE">Percentage (%)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Choose between fixed or percentage</p>
                </div>
              </div>
            </div>

            {/* Pricing Information Section */}
            <div className="space-y-4 p-4 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border border-purple-100">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <div className="h-1 w-1 rounded-full bg-purple-600" />
                Pricing & Distribution
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="value" className="text-sm font-semibold text-gray-700 mb-2 block">
                    Charge Value ({getChargeSymbol(formData.chargeType)}) *
                  </Label>
                  <Input
                    id="value"
                    name="value"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.value}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    className="border-slate-300 focus:ring-purple-500 focus:border-purple-500 font-semibold text-base"
                  />
                  <p className="text-xs text-gray-600 mt-1">Total charge amount</p>
                </div>

                <div>
                  <Label
                    htmlFor="actualValue"
                    className="text-sm font-semibold text-gray-700 mb-2 block"
                  >
                    Actual Value ({getChargeSymbol(formData.chargeType)}) *
                  </Label>
                  <Input
                    id="actualValue"
                    name="actualValue"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.actualValue}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    className="border-slate-300 focus:ring-purple-500 focus:border-purple-500 font-semibold text-base"
                  />
                  <p className="text-xs text-gray-600 mt-1">What the service actually costs</p>
                </div>

                <div>
                  <Label
                    htmlFor="vendorBears"
                    className="text-sm font-semibold text-gray-700 mb-2 block"
                  >
                    Vendor Bears ({getChargeSymbol(formData.chargeType)}) *
                  </Label>
                  <Input
                    id="vendorBears"
                    name="vendorBears"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.vendorBears}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    className="border-slate-300 focus:ring-purple-500 focus:border-purple-500 font-semibold text-base"
                  />
                  <p className="text-xs text-gray-600 mt-1">Amount you absorb</p>
                </div>
              </div>
            </div>

            {/* Additional Settings Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <div className="h-1 w-1 rounded-full bg-purple-600" />
                Settings & Configuration
              </h3>

              <div>
                <Label
                  htmlFor="displayOrder"
                  className="text-sm font-semibold text-gray-700 mb-2 block"
                >
                  Display Order
                </Label>
                <Input
                  id="displayOrder"
                  name="displayOrder"
                  type="number"
                  value={formData.displayOrder}
                  onChange={handleInputChange}
                  placeholder="0"
                  className="border-slate-300 focus:ring-purple-500 focus:border-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Control the order charges appear in lists (lower numbers first)
                </p>
              </div>

              <div className="space-y-3 pt-2 pb-2 bg-slate-50 rounded-lg p-4 border border-slate-200">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="chargeableToUser"
                    checked={formData.chargeableToUser}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-purple-600 border-slate-300 rounded focus:ring-purple-500 mt-0.5 flex-shrink-0"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-semibold text-gray-900 group-hover:text-gray-700">
                      Chargeable to User
                    </span>
                    <p className="text-xs text-gray-600 mt-0.5">
                      If enabled, this charge will be added to customer's invoice
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-purple-600 border-slate-300 rounded focus:ring-purple-500 mt-0.5 flex-shrink-0"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-semibold text-gray-900 group-hover:text-gray-700">
                      Active
                    </span>
                    <p className="text-xs text-gray-600 mt-0.5">Enable or disable this charge</p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t border-slate-200 pt-4 mt-4 gap-3 flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsAddDialogOpen(false);
                resetForm();
              }}
              className="border-slate-300 text-gray-700 hover:bg-slate-50 px-6"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleCreateCharge}
              className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white shadow-md hover:shadow-lg px-6"
            >
              <Check className="w-4 h-4 mr-2" />
              Create Charge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Charge Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-4 border-b border-slate-200">
            <DialogTitle className="text-xl font-bold text-gray-900">Edit Charge</DialogTitle>
            <DialogDescription className="mt-1">
              Update charge configuration. All values marked with * are required.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Basic Information Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <div className="h-1 w-1 rounded-full bg-purple-600" />
                Basic Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label
                    htmlFor="edit-name"
                    className="text-sm font-semibold text-gray-700 mb-2 block"
                  >
                    Charge Name *
                  </Label>
                  <select
                    id="edit-name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm font-medium focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  >
                    {CHARGE_NAME_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Select the type of charge</p>
                </div>

                <div>
                  <Label
                    htmlFor="edit-chargeType"
                    className="text-sm font-semibold text-gray-700 mb-2 block"
                  >
                    Charge Type *
                  </Label>
                  <select
                    id="edit-chargeType"
                    name="chargeType"
                    value={formData.chargeType}
                    onChange={handleInputChange}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm font-medium focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  >
                    <option value="FIXED">Fixed Amount (₹)</option>
                    <option value="PERCENTAGE">Percentage (%)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Choose between fixed or percentage</p>
                </div>
              </div>
            </div>

            {/* Pricing Information Section */}
            <div className="space-y-4 p-4 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border border-purple-100">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <div className="h-1 w-1 rounded-full bg-purple-600" />
                Pricing & Distribution
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label
                    htmlFor="edit-value"
                    className="text-sm font-semibold text-gray-700 mb-2 block"
                  >
                    Charge Value ({getChargeSymbol(formData.chargeType)}) *
                  </Label>
                  <Input
                    id="edit-value"
                    name="value"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.value}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    className="border-slate-300 focus:ring-purple-500 focus:border-purple-500 font-semibold text-base"
                  />
                  <p className="text-xs text-gray-600 mt-1">Total charge amount</p>
                </div>

                <div>
                  <Label
                    htmlFor="edit-actualValue"
                    className="text-sm font-semibold text-gray-700 mb-2 block"
                  >
                    Actual Value ({getChargeSymbol(formData.chargeType)}) *
                  </Label>
                  <Input
                    id="edit-actualValue"
                    name="actualValue"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.actualValue}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    className="border-slate-300 focus:ring-purple-500 focus:border-purple-500 font-semibold text-base"
                  />
                  <p className="text-xs text-gray-600 mt-1">What the service actually costs</p>
                </div>

                <div>
                  <Label
                    htmlFor="edit-vendorBears"
                    className="text-sm font-semibold text-gray-700 mb-2 block"
                  >
                    Vendor Bears ({getChargeSymbol(formData.chargeType)}) *
                  </Label>
                  <Input
                    id="edit-vendorBears"
                    name="vendorBears"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.vendorBears}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    className="border-slate-300 focus:ring-purple-500 focus:border-purple-500 font-semibold text-base"
                  />
                  <p className="text-xs text-gray-600 mt-1">Amount you absorb</p>
                </div>
              </div>
            </div>

            {/* Additional Settings Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <div className="h-1 w-1 rounded-full bg-purple-600" />
                Settings & Configuration
              </h3>

              <div>
                <Label
                  htmlFor="edit-displayOrder"
                  className="text-sm font-semibold text-gray-700 mb-2 block"
                >
                  Display Order
                </Label>
                <Input
                  id="edit-displayOrder"
                  name="displayOrder"
                  type="number"
                  value={formData.displayOrder}
                  onChange={handleInputChange}
                  placeholder="0"
                  className="border-slate-300 focus:ring-purple-500 focus:border-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Control the order charges appear in lists (lower numbers first)
                </p>
              </div>

              <div className="space-y-3 pt-2 pb-2 bg-slate-50 rounded-lg p-4 border border-slate-200">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="chargeableToUser"
                    checked={formData.chargeableToUser}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-purple-600 border-slate-300 rounded focus:ring-purple-500 mt-0.5 flex-shrink-0"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-semibold text-gray-900 group-hover:text-gray-700">
                      Chargeable to User
                    </span>
                    <p className="text-xs text-gray-600 mt-0.5">
                      If enabled, this charge will be added to customer's invoice
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-purple-600 border-slate-300 rounded focus:ring-purple-500 mt-0.5 flex-shrink-0"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-semibold text-gray-900 group-hover:text-gray-700">
                      Active
                    </span>
                    <p className="text-xs text-gray-600 mt-0.5">Enable or disable this charge</p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t border-slate-200 pt-4 mt-4 gap-3 flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsEditDialogOpen(false);
                resetForm();
              }}
              className="border-slate-300 text-gray-700 hover:bg-slate-50 px-6"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleUpdateCharge}
              className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white shadow-md hover:shadow-lg px-6"
            >
              <Check className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div className="text-center">
              <DialogTitle className="text-lg">Delete Charge</DialogTitle>
              <DialogDescription className="mt-2">
                Are you sure you want to delete this charge? This action cannot be undone.
              </DialogDescription>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setDeleteConfirmId(null);
              }}
              className="flex-1 border-slate-300 text-gray-700 hover:bg-slate-50"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDeleteCharge(deleteConfirmId)}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
}
